/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import usePermissions from "../hooks/usePermissions";

const API = process.env.REACT_APP_API_URL || "/api";

const MODULES = [
  "dashboard",
  "users",
  "devices",
  "products",
  "roles",
  "reports",
  "alerts",
  "maintenance",
  "esg",
];

const ACTIONS = ["view", "create", "edit", "delete"];

const Roles = () => {
  const token = localStorage.getItem("token");
  const { canView, canCreate, canEdit, canDelete } = usePermissions();

  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rolesError, setRolesError] = useState(null);

  const [newRole, setNewRole] = useState("");
  const [description, setDescription] = useState("");

  /* ================= FETCH ROLES ================= */
  const fetchRoles = async () => {
    setRolesError(null);
    try {
      const res = await fetch(`${API}/roles`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.error("fetchRoles failed:", res.status, err.message);
        setRolesError(`Failed to load roles (${res.status})`);
        return;
      }

      const data = await res.json();
      setRoles(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("fetchRoles error:", err);
      setRolesError("Network error — could not fetch roles");
    }
  };

  /* ================= FETCH PERMISSIONS ================= */
  const fetchPermissions = async (roleId) => {
    try {
      setLoading(true);

      const res = await fetch(`${API}/roles/${roleId}/permissions`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        console.error("fetchPermissions failed:", res.status);
        return;
      }

      const data = await res.json();
      const formatted = data.map((p) => `${p.module}:${p.action}`);
      setPermissions(formatted);
    } catch (err) {
      console.error("fetchPermissions error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  // Roles are loaded once when the page opens.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ================= TOGGLE PERMISSION ================= */
  const togglePermission = (module, action) => {
    const key = `${module}:${action}`;
    setPermissions((prev) =>
      prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]
    );
  };

  /* ================= SAVE PERMISSIONS ================= */
  const savePermissions = async () => {
    if (!selectedRole) return;

    const payload = permissions.map((p) => {
      const [module, action] = p.split(":");
      return { module, action };
    });

    try {
      const res = await fetch(`${API}/roles/${selectedRole}/permissions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ permissions: payload }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(`Failed to save permissions: ${err.message || res.status}`);
        return;
      }

      alert("Permissions updated");
    } catch (err) {
      console.error("savePermissions error:", err);
      alert("Network error — could not save permissions");
    }
  };

  /* ================= CREATE ROLE ================= */
  const handleAddRole = async () => {
    if (!newRole.trim()) return;

    try {
      const res = await fetch(`${API}/roles`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: newRole.trim(), description }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(`Failed to create role: ${err.message || res.status}`);
        return;
      }

      setNewRole("");
      setDescription("");
      fetchRoles();
    } catch (err) {
      console.error("handleAddRole error:", err);
      alert("Network error — could not create role");
    }
  };

  /* ================= DELETE ROLE ================= */
  const handleDelete = async (id) => {
    if (!canDelete("roles")) return;
    if (!window.confirm("Delete role?")) return;

    try {
      const res = await fetch(`${API}/roles/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(`Failed to delete role: ${err.message || res.status}`);
        return;
      }

      if (selectedRole === id) {
        setSelectedRole(null);
        setPermissions([]);
      }

      fetchRoles();
    } catch (err) {
      console.error("handleDelete error:", err);
      alert("Network error — could not delete role");
    }
  };

  /* ================= ACCESS GUARD ================= */
  if (!canView("roles")) {
    return <div className="p-6 text-red-500">Access Denied</div>;
  }

  /* ================= UI ================= */
  return (
    <div className="p-6 space-y-6">

      {/* HEADER */}
      <div className="flex justify-between items-center">
        <h1 className="text-xl font-semibold">Roles Management</h1>

        {canCreate("roles") && (
          <button
            onClick={handleAddRole}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm"
          >
            + Add Role
          </button>
        )}
      </div>

      {/* ADD ROLE FORM */}
      {canCreate("roles") && (
        <div className="flex gap-3">
          <input
            placeholder="Role Name"
            value={newRole}
            onChange={(e) => setNewRole(e.target.value)}
            className="border p-2 rounded"
          />
          <input
            placeholder="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="border p-2 rounded"
          />
        </div>
      )}

      {/* ERROR STATE */}
      {rolesError && (
        <div className="text-red-500 text-sm bg-red-50 border border-red-200 rounded p-3">
          {rolesError}
        </div>
      )}

      {/* EMPTY STATE */}
      {!rolesError && roles.length === 0 && (
        <div className="text-gray-400 text-sm">No roles found.</div>
      )}

      {/* ROLES LIST */}
      <div className="grid grid-cols-3 gap-4">
        {roles.map((r) => (
          <div
            key={r.id}
            onClick={() => {
              setSelectedRole(r.id);
              fetchPermissions(r.id);
            }}
            className={`p-4 border rounded cursor-pointer ${
              selectedRole === r.id ? "bg-blue-50 border-blue-300" : "bg-white"
            }`}
          >
            <h2 className="font-semibold">{r.name}</h2>
            <p className="text-sm text-gray-500">{r.description}</p>

            {canDelete("roles") && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(r.id);
                }}
                className="text-red-500 text-sm mt-2"
              >
                Delete
              </button>
            )}
          </div>
        ))}
      </div>

      {/* PERMISSIONS GRID */}
      {selectedRole && (
        <div className="bg-white p-6 rounded-xl border">
          <h2 className="mb-4 font-semibold">Permissions</h2>

          {loading ? (
            <p className="text-sm text-gray-400">Loading...</p>
          ) : (
            <div className="space-y-3">
              {MODULES.map((module) => (
                <div key={module} className="flex items-center gap-4">
                  <div className="w-32 capitalize text-sm font-medium">
                    {module}
                  </div>

                  {ACTIONS.map((action) => {
                    const key = `${module}:${action}`;
                    return (
                      <label key={key} className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={permissions.includes(key)}
                          onChange={() => togglePermission(module, action)}
                          disabled={!canEdit("roles")}
                        />
                        <span className="text-sm">{action}</span>
                      </label>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          {canEdit("roles") && (
            <button
              onClick={savePermissions}
              className="mt-4 bg-green-600 text-white px-4 py-2 rounded"
            >
              Save Permissions
            </button>
          )}
        </div>
      )}

    </div>
  );
};

export default Roles;
